import { useCallback, useMemo } from 'react';
import { useUserRoles } from '@features/user/store/hooks';
import { hasAdminPrivileges } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useConvertToOrgTemplateMutation,
  useCreateOrgTemplateMutation,
  useDeleteOrgTemplateMutation,
  useGetOrgTemplatesQuery,
} from '../store';
import type { CreateOrgNoteTemplateRequest, OrgNoteTemplateInfo } from '../types';

interface UseOrgTemplatesReturn {
  /** List of organization templates */
  templates: OrgNoteTemplateInfo[];
  /** Whether templates are loading */
  isLoading: boolean;
  /** Whether the current user is an organization admin */
  isOrgAdmin: boolean;
  /** Create a new organization template */
  createOrgTemplate: (request: CreateOrgNoteTemplateRequest) => Promise<string | null>;
  /** Convert a user template to organization scope */
  convertToOrgTemplate: (templateId: string) => Promise<boolean>;
  /** Delete an organization template */
  deleteOrgTemplate: (templateId: string) => Promise<boolean>;
  /** Refetch the list */
  refetch: () => void;
}

export const useOrgTemplates = (): UseOrgTemplatesReturn => {
  const { enqueueSnackbar } = useSnackbar();
  const userRoles = useUserRoles();
  
  // Check if user has org admin privileges
  const isOrgAdmin = useMemo(() => hasAdminPrivileges(userRoles), [userRoles]);

  // Only fetch if the user is an org admin
  const {
    data: templatesData,
    isLoading,
    refetch,
  } = useGetOrgTemplatesQuery(undefined, {
    skip: !isOrgAdmin,
  });

  const [createMutation] = useCreateOrgTemplateMutation();
  const [convertMutation] = useConvertToOrgTemplateMutation();
  const [deleteMutation] = useDeleteOrgTemplateMutation();

  const templates = useMemo(() => templatesData?.templates || [], [templatesData]);

  const createOrgTemplate = useCallback(
    async (request: CreateOrgNoteTemplateRequest): Promise<string | null> => {
      try {
        const result = await createMutation({
          createOrgNoteTemplateRequest: request,
        }).unwrap();
        enqueueSnackbar('Organization template created successfully', { variant: 'success' });
        return result.template.id;
      } catch (error: any) {
        console.error('Failed to create organization template:', error);
        enqueueSnackbar(error?.data?.message || 'Failed to create organization template', {
          variant: 'error',
        });
        return null;
      }
    },
    [createMutation, enqueueSnackbar],
  );

  const convertToOrgTemplate = useCallback(
    async (templateId: string): Promise<boolean> => {
      try {
        await convertMutation({
          convertToOrgTemplateRequest: { template_id: templateId },
        }).unwrap();
        enqueueSnackbar('Template shared with organization successfully', { variant: 'success' });
        return true;
      } catch (error: any) {
        console.error('Failed to convert template to organization scope:', error);
        enqueueSnackbar(error?.data?.message || 'Failed to share template with organization', {
          variant: 'error',
        });
        return false;
      }
    },
    [convertMutation, enqueueSnackbar],
  );

  const deleteOrgTemplate = useCallback(
    async (templateId: string): Promise<boolean> => {
      try {
        await deleteMutation({ templateId }).unwrap();
        enqueueSnackbar('Organization template deleted successfully', { variant: 'success' });
        return true;
      } catch (error: any) {
        console.error('Failed to delete organization template:', error);
        enqueueSnackbar(error?.data?.message || 'Failed to delete organization template', {
          variant: 'error',
        });
        return false;
      }
    },
    [deleteMutation, enqueueSnackbar],
  );

  return {
    templates,
    isLoading,
    isOrgAdmin,
    createOrgTemplate,
    convertToOrgTemplate,
    deleteOrgTemplate,
    refetch,
  };
};

